import { useQuery } from "@tanstack/react-query";
import { PageBreadcrumbs } from "@/components/shared/page-breadcrumbs";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/status-badge";
import { apiGet } from "@/lib/api-client";
import { teamKeys } from "@/lib/query";
import { unboundRecordArraySchema } from "@/schemas/teams";

const SKELETON_ROWS = ["sk-0", "sk-1", "sk-2", "sk-3", "sk-4", "sk-5", "sk-6", "sk-7"] as const;

export function ClaimTeamsUnboundPage() {
  const unboundQuery = useQuery({
    queryKey: teamKeys.unbound(),
    queryFn: () => apiGet("/api/claim-team-history", unboundRecordArraySchema),
  });

  return (
    <div className="space-y-6">
      <PageBreadcrumbs current="All Teams" />
      <div className="rounded-md border bg-card p-4">
        <h1 className="font-display text-3xl font-bold">Claim Teams Unbound</h1>
        <p className="mt-1 text-muted-foreground">All team assignments in a flat view.</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <h3 className="font-semibold">All Team Assignments</h3>
        </CardHeader>
        <CardContent>
          {unboundQuery.isLoading && (
            <div className="space-y-2">
              {SKELETON_ROWS.map((key) => (
                <div key={key} className="h-8 animate-pulse rounded bg-muted" />
              ))}
            </div>
          )}

          {unboundQuery.isError && (
            <p className="text-sm text-destructive">
              Failed to load team assignments.{" "}
              <button type="button" className="underline" onClick={() => unboundQuery.refetch()}>
                Retry
              </button>
            </p>
          )}

          {unboundQuery.data && unboundQuery.data.length === 0 && (
            <p className="text-sm text-muted-foreground">No team assignments found.</p>
          )}

          {unboundQuery.data && unboundQuery.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 font-medium">Region</th>
                    <th className="pb-2 font-medium">Team</th>
                    <th className="pb-2 font-medium">User ID</th>
                    <th className="pb-2 font-medium">Title</th>
                    <th className="pb-2 font-medium">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {unboundQuery.data.map((record) => (
                    <tr key={record.id} className="border-b last:border-0">
                      <td className="py-2">{record.claimRegion}</td>
                      <td className="py-2">{record.claimRegionTeam}</td>
                      <td className="py-2">{record.userId}</td>
                      <td className="py-2">{record.title}</td>
                      <td className="py-2">
                        <StatusBadge
                          statusKey={record.isActive ? "policy-active" : "policy-expired"}
                          label={record.isActive ? "Yes" : "No"}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
